/**
 * Custom Theme Presets
 *
 * Defines preset color overrides that users can apply to customize their theme.
 * These work by injecting CSS variable overrides on top of Chakra's generated variables.
 */

export type ThemePresetCategory = 'accent' | 'full';
export interface ThemePreset {
	id: string;
	name: string;
	category: ThemePresetCategory;
	description: string;
	/** Preview colors for the modal UI */
	preview: {
		primary: string;
		secondary: string;
		accent: string;
	};
	/** CSS variable overrides - applied to :root */
	overrides: Record<string, string>;
}

// Color values from colors.ts for direct use
const colors = {
	purple: {
		'100': '#dbd7f9ff',
		'200': '#bab2f5ff',
		'300': '#9c8ef1ff',
		'400': '#8c75ebff',
		'500': '#7b5ce0ff',
		'600': '#6940ceff',
		'700': '#5036a6ff',
		'800': '#3a2579ff',
	},
	purpleAlpha: {
		'100': '#7b5ce014',
		'200': '#7b5ce01f',
		'300': '#7b5ce033',
	},
	teal: {
		'300': '#44c0b5ff',
		'400': '#279f95ff',
		'500': '#1d7f77ff',
		'600': '#0b5f59ff',
		'700': '#004944ff',
		'800': '#003532ff',
	},
	tealAlpha: {
		'100': '#279f9514',
		'200': '#279f9529',
		'300': '#279f953d',
	},
	pink: {
		'200': '#feafcbff',
		'300': '#fa82afff',
		'400': '#e5548fff',
		'500': '#c03670ff',
		'600': '#9e2b5cff',
		'700': '#7b1f49ff',
		'800': '#611139ff',
	},
	pinkAlpha: {
		'100': '#e5548f14',
		'200': '#e5548f29',
		'300': '#e5548f3d',
	},
	orange: {
		'300': '#ff8b1bff',
		'400': '#df6507ff',
		'500': '#b44a0bff',
		'600': '#8a3605ff',
		'700': '#762c00ff',
	},
	orangeAlpha: {
		'100': '#ff650714',
		'200': '#ff650729',
		'300': '#ff65073d',
	},
	coral: {
		'300': '#fd8c73ff',
		'400': '#f0664aff',
		'500': '#c4432bff',
		'600': '#9e2f1cff',
		'700': '#861f0fff',
	},
	coralAlpha: {
		'100': '#f0664a14',
		'200': '#f0664a29',
		'300': '#f0664a3d',
	},
	green: {
		'200': '#7ccb9bff',
		'300': '#4bb475ff',
		'400': '#1e9f54ff',
		'500': '#118342ff',
		'600': '#066a35ff',
		'700': '#055229ff',
		'800': '#073b21ff',
	},
	greenAlpha: {
		'100': '#11834214',
		'200': '#11834229',
		'300': '#1183423d',
	},
	blue: {
		'500': '#1670dfff',
		'600': '#1358b9ff',
		'700': '#14458aff',
		'800': '#103560ff',
		'900': '#08253fff',
	},
	yellow: {
		'500': '#b37e00ff',
		'600': '#916402ff',
		'700': '#5e4003ff',
	},
};

/**
 * Accent Color Swaps
 * These replace the primary (blue) color with a different accent color
 */
const accentPresets: ThemePreset[] = [
	{
		id: 'purple-accent',
		name: 'Purple',
		category: 'accent',
		description: 'Purple as the primary accent color',
		preview: {
			primary: colors.purple['500'],
			secondary: colors.purple['600'],
			accent: colors.purple['700'],
		},
		overrides: {
			// Primary colors
			'--chakra-colors-primary-fg': colors.purple['500'],
			'--chakra-colors-primary-fgStrong': colors.purple['600'],
			'--chakra-colors-primary-fgStronger': colors.purple['700'],
			'--chakra-colors-primary-fgStrongest': colors.purple['800'],
			'--chakra-colors-primary-bgAlpha': colors.purpleAlpha['100'],
			'--chakra-colors-primary-bgAlphaHover': colors.purpleAlpha['200'],
			'--chakra-colors-primary-bgAlphaActive': colors.purpleAlpha['300'],
			'--chakra-colors-primary-bg': colors.purple['500'],
			'--chakra-colors-primary-bgHover': colors.purple['600'],
			'--chakra-colors-primary-bgActive': colors.purple['700'],
			// Interactive text
			'--chakra-colors-text-interactive': colors.purple['500'],
			// Border focus
			'--chakra-colors-border-focus': colors.purple['500'],
		},
	},
	{
		id: 'teal-accent',
		name: 'Teal',
		category: 'accent',
		description: 'Teal as the primary accent color',
		preview: {
			primary: colors.teal['500'],
			secondary: colors.teal['600'],
			accent: colors.teal['400'],
		},
		overrides: {
			'--chakra-colors-primary-fg': colors.teal['500'],
			'--chakra-colors-primary-fgStrong': colors.teal['600'],
			'--chakra-colors-primary-fgStronger': colors.teal['700'],
			'--chakra-colors-primary-fgStrongest': colors.teal['800'],
			'--chakra-colors-primary-bgAlpha': colors.tealAlpha['100'],
			'--chakra-colors-primary-bgAlphaHover': colors.tealAlpha['200'],
			'--chakra-colors-primary-bgAlphaActive': colors.tealAlpha['300'],
			'--chakra-colors-primary-bg': colors.teal['500'],
			'--chakra-colors-primary-bgHover': colors.teal['600'],
			'--chakra-colors-primary-bgActive': colors.teal['700'],
			'--chakra-colors-text-interactive': colors.teal['500'],
			'--chakra-colors-border-focus': colors.teal['500'],
		},
	},
	{
		id: 'pink-accent',
		name: 'Rose',
		category: 'accent',
		description: 'Rose/pink as the primary accent color',
		preview: {
			primary: colors.pink['500'],
			secondary: colors.pink['600'],
			accent: colors.pink['400'],
		},
		overrides: {
			'--chakra-colors-primary-fg': colors.pink['500'],
			'--chakra-colors-primary-fgStrong': colors.pink['600'],
			'--chakra-colors-primary-fgStronger': colors.pink['700'],
			'--chakra-colors-primary-fgStrongest': colors.pink['800'],
			'--chakra-colors-primary-bgAlpha': colors.pinkAlpha['100'],
			'--chakra-colors-primary-bgAlphaHover': colors.pinkAlpha['200'],
			'--chakra-colors-primary-bgAlphaActive': colors.pinkAlpha['300'],
			'--chakra-colors-primary-bg': colors.pink['500'],
			'--chakra-colors-primary-bgHover': colors.pink['600'],
			'--chakra-colors-primary-bgActive': colors.pink['700'],
			'--chakra-colors-text-interactive': colors.pink['500'],
			'--chakra-colors-border-focus': colors.pink['500'],
		},
	},
	{
		id: 'orange-accent',
		name: 'Orange',
		category: 'accent',
		description: 'Orange as the primary accent color',
		preview: {
			primary: colors.orange['400'],
			secondary: colors.orange['500'],
			accent: colors.orange['300'],
		},
		overrides: {
			'--chakra-colors-primary-fg': colors.orange['400'],
			'--chakra-colors-primary-fgStrong': colors.orange['500'],
			'--chakra-colors-primary-fgStronger': colors.orange['600'],
			'--chakra-colors-primary-fgStrongest': colors.orange['700'],
			'--chakra-colors-primary-bgAlpha': colors.orangeAlpha['100'],
			'--chakra-colors-primary-bgAlphaHover': colors.orangeAlpha['200'],
			'--chakra-colors-primary-bgAlphaActive': colors.orangeAlpha['300'],
			'--chakra-colors-primary-bg': colors.orange['400'],
			'--chakra-colors-primary-bgHover': colors.orange['500'],
			'--chakra-colors-primary-bgActive': colors.orange['600'],
			'--chakra-colors-text-interactive': colors.orange['400'],
			'--chakra-colors-border-focus': colors.orange['400'],
		},
	},
	{
		id: 'coral-accent',
		name: 'Coral',
		category: 'accent',
		description: 'Coral as the primary accent color',
		preview: {
			primary: colors.coral['400'],
			secondary: colors.coral['500'],
			accent: colors.coral['300'],
		},
		overrides: {
			'--chakra-colors-primary-fg': colors.coral['400'],
			'--chakra-colors-primary-fgStrong': colors.coral['500'],
			'--chakra-colors-primary-fgStronger': colors.coral['600'],
			'--chakra-colors-primary-fgStrongest': colors.coral['700'],
			'--chakra-colors-primary-bgAlpha': colors.coralAlpha['100'],
			'--chakra-colors-primary-bgAlphaHover': colors.coralAlpha['200'],
			'--chakra-colors-primary-bgAlphaActive': colors.coralAlpha['300'],
			'--chakra-colors-primary-bg': colors.coral['400'],
			'--chakra-colors-primary-bgHover': colors.coral['500'],
			'--chakra-colors-primary-bgActive': colors.coral['600'],
			'--chakra-colors-text-interactive': colors.coral['400'],
			'--chakra-colors-border-focus': colors.coral['400'],
		},
	},
];

/**
 * Curated Full Themes
 * These provide complete palette overrides including backgrounds, surfaces, and text colors
 * for a truly unique visual experience
 */
const fullThemePresets: ThemePreset[] = [
	{
		id: 'ocean',
		name: 'Ocean',
		category: 'full',
		description: 'Deep blue waters with teal accents',
		preview: {
			primary: '#0a2540',
			secondary: '#1a4a6e',
			accent: colors.teal['400'],
		},
		overrides: {
			// Backgrounds - deep ocean blues
			'--chakra-colors-bg-canvas': '#0d3b5c',
			'--chakra-colors-bg-surface': '#0a2540',
			'--chakra-colors-bg-inset': '#0d3b5c',
			'--chakra-colors-bg-soft': '#1a4a6e',
			'--chakra-colors-bg-subtle': '#082030',
			'--chakra-colors-bg-topNav': '#0a2540',
			// Text - light colors for dark bg
			'--chakra-colors-text-default': '#e8f4fc',
			'--chakra-colors-text-soft': '#a8d4f0',
			'--chakra-colors-text-softer': '#7bb8dc',
			'--chakra-colors-text-softest': '#5a9cc4',
			'--chakra-colors-text-onColor': '#ffffff',
			// Borders
			'--chakra-colors-border-default': 'rgba(168, 212, 240, 0.25)',
			'--chakra-colors-border-soft': 'rgba(168, 212, 240, 0.15)',
			'--chakra-colors-border-strong': 'rgba(168, 212, 240, 0.4)',
			// Primary - bright teal
			'--chakra-colors-primary-fg': colors.teal['400'],
			'--chakra-colors-primary-fgStrong': colors.teal['300'],
			'--chakra-colors-primary-bg': colors.teal['500'],
			'--chakra-colors-primary-bgHover': colors.teal['400'],
			'--chakra-colors-primary-bgActive': colors.teal['600'],
			'--chakra-colors-primary-bgAlpha': 'rgba(39, 159, 149, 0.2)',
			'--chakra-colors-primary-bgAlphaHover': 'rgba(39, 159, 149, 0.3)',
			// Secondary
			'--chakra-colors-secondary-bg': '#e8f4fc',
			'--chakra-colors-secondary-bgHover': '#d0e8f5',
			'--chakra-colors-secondary-fg': '#e8f4fc',
			'--chakra-colors-secondary-fgOnSurface': '#0a2540',
			'--chakra-colors-secondary-bgAlpha': 'rgba(232, 244, 252, 0.15)',
			'--chakra-colors-secondary-bgAlphaHover': 'rgba(232, 244, 252, 0.25)',
			// Points - gold
			'--chakra-colors-points-fg': '#ffd700',
			'--chakra-colors-points-fgHover': '#ffed4a',
			'--chakra-colors-points-fgOnSurface': '#ffd700',
			'--chakra-colors-points-bgAlpha': 'rgba(255, 215, 0, 0.2)',
			// Hashtags - cyan
			'--chakra-colors-hashtag-fg': '#00d4ff',
			'--chakra-colors-hashtag-fgOnSurface': '#00d4ff',
			'--chakra-colors-text-hashtag': '#00d4ff',
			// Interactive
			'--chakra-colors-text-interactive': colors.teal['300'],
			'--chakra-colors-border-focus': colors.teal['400'],
		},
	},
	{
		id: 'sunset',
		name: 'Sunset',
		category: 'full',
		description: 'Warm golden hour with coral and amber',
		preview: {
			primary: '#2d1810',
			secondary: '#4a2a1a',
			accent: colors.orange['300'],
		},
		overrides: {
			// Backgrounds - warm sunset gradients
			'--chakra-colors-bg-canvas': '#3d1f12',
			'--chakra-colors-bg-surface': '#2d1810',
			'--chakra-colors-bg-inset': '#4a2a1a',
			'--chakra-colors-bg-soft': '#5c3520',
			'--chakra-colors-bg-subtle': '#1f0f08',
			'--chakra-colors-bg-topNav': '#2d1810',
			// Text - warm cream colors
			'--chakra-colors-text-default': '#fff5e6',
			'--chakra-colors-text-soft': '#f0d9b5',
			'--chakra-colors-text-softer': '#d4b896',
			'--chakra-colors-text-softest': '#b89878',
			'--chakra-colors-text-onColor': '#ffffff',
			// Borders
			'--chakra-colors-border-default': 'rgba(240, 217, 181, 0.25)',
			'--chakra-colors-border-soft': 'rgba(240, 217, 181, 0.15)',
			'--chakra-colors-border-strong': 'rgba(240, 217, 181, 0.4)',
			// Primary - coral orange
			'--chakra-colors-primary-fg': colors.coral['400'],
			'--chakra-colors-primary-fgStrong': colors.coral['300'],
			'--chakra-colors-primary-bg': colors.coral['500'],
			'--chakra-colors-primary-bgHover': colors.coral['400'],
			'--chakra-colors-primary-bgActive': colors.coral['600'],
			'--chakra-colors-primary-bgAlpha': 'rgba(240, 102, 74, 0.25)',
			'--chakra-colors-primary-bgAlphaHover': 'rgba(240, 102, 74, 0.35)',
			// Secondary
			'--chakra-colors-secondary-bg': '#fff5e6',
			'--chakra-colors-secondary-bgHover': '#ffe8cc',
			'--chakra-colors-secondary-fg': '#fff5e6',
			'--chakra-colors-secondary-fgOnSurface': '#2d1810',
			'--chakra-colors-secondary-bgAlpha': 'rgba(255, 245, 230, 0.15)',
			'--chakra-colors-secondary-bgAlphaHover': 'rgba(255, 245, 230, 0.25)',
			// Points - golden yellow
			'--chakra-colors-points-fg': '#ffb700',
			'--chakra-colors-points-fgHover': '#ffc933',
			'--chakra-colors-points-fgOnSurface': '#ffb700',
			'--chakra-colors-points-bgAlpha': 'rgba(255, 183, 0, 0.25)',
			// Hashtags - amber
			'--chakra-colors-hashtag-fg': '#ff9500',
			'--chakra-colors-hashtag-fgOnSurface': '#ff9500',
			'--chakra-colors-text-hashtag': '#ff9500',
			// Interactive
			'--chakra-colors-text-interactive': colors.orange['300'],
			'--chakra-colors-border-focus': colors.coral['400'],
		},
	},
	{
		id: 'forest',
		name: 'Forest',
		category: 'full',
		description: 'Deep woodland greens with earthy browns',
		preview: {
			primary: '#0f1f15',
			secondary: '#1a3020',
			accent: colors.green['400'],
		},
		overrides: {
			// Backgrounds - forest greens
			'--chakra-colors-bg-canvas': '#162618',
			'--chakra-colors-bg-surface': '#0f1f15',
			'--chakra-colors-bg-inset': '#1a3020',
			'--chakra-colors-bg-soft': '#254030',
			'--chakra-colors-bg-subtle': '#0a1510',
			'--chakra-colors-bg-topNav': '#0f1f15',
			// Text - soft sage
			'--chakra-colors-text-default': '#e8f0e6',
			'--chakra-colors-text-soft': '#b8d4b0',
			'--chakra-colors-text-softer': '#90b888',
			'--chakra-colors-text-softest': '#6a9960',
			'--chakra-colors-text-onColor': '#ffffff',
			// Borders
			'--chakra-colors-border-default': 'rgba(184, 212, 176, 0.25)',
			'--chakra-colors-border-soft': 'rgba(184, 212, 176, 0.15)',
			'--chakra-colors-border-strong': 'rgba(184, 212, 176, 0.4)',
			// Primary - bright green
			'--chakra-colors-primary-fg': colors.green['400'],
			'--chakra-colors-primary-fgStrong': colors.green['300'],
			'--chakra-colors-primary-bg': colors.green['500'],
			'--chakra-colors-primary-bgHover': colors.green['400'],
			'--chakra-colors-primary-bgActive': colors.green['600'],
			'--chakra-colors-primary-bgAlpha': 'rgba(30, 159, 84, 0.25)',
			'--chakra-colors-primary-bgAlphaHover': 'rgba(30, 159, 84, 0.35)',
			// Secondary
			'--chakra-colors-secondary-bg': '#e8f0e6',
			'--chakra-colors-secondary-bgHover': '#d4e8d0',
			'--chakra-colors-secondary-fg': '#e8f0e6',
			'--chakra-colors-secondary-fgOnSurface': '#0f1f15',
			'--chakra-colors-secondary-bgAlpha': 'rgba(232, 240, 230, 0.15)',
			'--chakra-colors-secondary-bgAlphaHover': 'rgba(232, 240, 230, 0.25)',
			// Points - golden brown
			'--chakra-colors-points-fg': '#d4a574',
			'--chakra-colors-points-fgHover': '#e0b888',
			'--chakra-colors-points-fgOnSurface': '#d4a574',
			'--chakra-colors-points-bgAlpha': 'rgba(212, 165, 116, 0.25)',
			// Hashtags - moss green
			'--chakra-colors-hashtag-fg': '#7cb342',
			'--chakra-colors-hashtag-fgOnSurface': '#7cb342',
			'--chakra-colors-text-hashtag': '#7cb342',
			// Interactive
			'--chakra-colors-text-interactive': colors.green['300'],
			'--chakra-colors-border-focus': colors.green['400'],
		},
	},
	{
		id: 'lavender',
		name: 'Lavender',
		category: 'full',
		description: 'Dreamy purple twilight with pink accents',
		preview: {
			primary: '#1a1025',
			secondary: '#2a1a3a',
			accent: colors.purple['300'],
		},
		overrides: {
			// Backgrounds - deep purple
			'--chakra-colors-bg-canvas': '#251838',
			'--chakra-colors-bg-surface': '#1a1025',
			'--chakra-colors-bg-inset': '#2a1a3a',
			'--chakra-colors-bg-soft': '#3a2850',
			'--chakra-colors-bg-subtle': '#10081a',
			'--chakra-colors-bg-topNav': '#1a1025',
			// Text - soft lavender
			'--chakra-colors-text-default': '#f0e8f8',
			'--chakra-colors-text-soft': '#d0b8e8',
			'--chakra-colors-text-softer': '#b090d0',
			'--chakra-colors-text-softest': '#9070b0',
			'--chakra-colors-text-onColor': '#ffffff',
			// Borders
			'--chakra-colors-border-default': 'rgba(208, 184, 232, 0.25)',
			'--chakra-colors-border-soft': 'rgba(208, 184, 232, 0.15)',
			'--chakra-colors-border-strong': 'rgba(208, 184, 232, 0.4)',
			// Primary - bright purple
			'--chakra-colors-primary-fg': colors.purple['400'],
			'--chakra-colors-primary-fgStrong': colors.purple['300'],
			'--chakra-colors-primary-bg': colors.purple['500'],
			'--chakra-colors-primary-bgHover': colors.purple['400'],
			'--chakra-colors-primary-bgActive': colors.purple['600'],
			'--chakra-colors-primary-bgAlpha': 'rgba(123, 92, 224, 0.25)',
			'--chakra-colors-primary-bgAlphaHover': 'rgba(123, 92, 224, 0.35)',
			// Secondary
			'--chakra-colors-secondary-bg': '#f0e8f8',
			'--chakra-colors-secondary-bgHover': '#e0d0f0',
			'--chakra-colors-secondary-fg': '#f0e8f8',
			'--chakra-colors-secondary-fgOnSurface': '#1a1025',
			'--chakra-colors-secondary-bgAlpha': 'rgba(240, 232, 248, 0.15)',
			'--chakra-colors-secondary-bgAlphaHover': 'rgba(240, 232, 248, 0.25)',
			// Points - pink
			'--chakra-colors-points-fg': colors.pink['400'],
			'--chakra-colors-points-fgHover': colors.pink['300'],
			'--chakra-colors-points-fgOnSurface': colors.pink['400'],
			'--chakra-colors-points-bgAlpha': 'rgba(229, 84, 143, 0.25)',
			// Hashtags - magenta
			'--chakra-colors-hashtag-fg': '#e040fb',
			'--chakra-colors-hashtag-fgOnSurface': '#e040fb',
			'--chakra-colors-text-hashtag': '#e040fb',
			// Interactive
			'--chakra-colors-text-interactive': colors.purple['300'],
			'--chakra-colors-border-focus': colors.purple['400'],
		},
	},
	{
		id: 'midnight',
		name: 'Midnight',
		category: 'full',
		description: 'Pure darkness with neon accents',
		preview: {
			primary: '#0a0a0c',
			secondary: '#141418',
			accent: '#00ff88',
		},
		overrides: {
			// Backgrounds - near black
			'--chakra-colors-bg-canvas': '#0f0f12',
			'--chakra-colors-bg-surface': '#0a0a0c',
			'--chakra-colors-bg-inset': '#141418',
			'--chakra-colors-bg-soft': '#1a1a20',
			'--chakra-colors-bg-subtle': '#050506',
			'--chakra-colors-bg-topNav': '#0a0a0c',
			// Text - cool white
			'--chakra-colors-text-default': '#e8e8ec',
			'--chakra-colors-text-soft': '#a0a0a8',
			'--chakra-colors-text-softer': '#707078',
			'--chakra-colors-text-softest': '#505058',
			'--chakra-colors-text-onColor': '#0a0a0c',
			// Borders
			'--chakra-colors-border-default': 'rgba(160, 160, 168, 0.2)',
			'--chakra-colors-border-soft': 'rgba(160, 160, 168, 0.1)',
			'--chakra-colors-border-strong': 'rgba(160, 160, 168, 0.35)',
			// Primary - neon green
			'--chakra-colors-primary-fg': '#00ff88',
			'--chakra-colors-primary-fgStrong': '#00cc6a',
			'--chakra-colors-primary-bg': '#00dd77',
			'--chakra-colors-primary-bgHover': '#00ff88',
			'--chakra-colors-primary-bgActive': '#00bb66',
			'--chakra-colors-primary-bgAlpha': 'rgba(0, 255, 136, 0.15)',
			'--chakra-colors-primary-bgAlphaHover': 'rgba(0, 255, 136, 0.25)',
			// Secondary
			'--chakra-colors-secondary-bg': '#e8e8ec',
			'--chakra-colors-secondary-bgHover': '#d0d0d8',
			'--chakra-colors-secondary-fg': '#e8e8ec',
			'--chakra-colors-secondary-fgOnSurface': '#0a0a0c',
			'--chakra-colors-secondary-bgAlpha': 'rgba(232, 232, 236, 0.1)',
			'--chakra-colors-secondary-bgAlphaHover': 'rgba(232, 232, 236, 0.2)',
			// Points - neon cyan
			'--chakra-colors-points-fg': '#00ffff',
			'--chakra-colors-points-fgHover': '#66ffff',
			'--chakra-colors-points-fgOnSurface': '#00ffff',
			'--chakra-colors-points-bgAlpha': 'rgba(0, 255, 255, 0.15)',
			// Hashtags - neon magenta
			'--chakra-colors-hashtag-fg': '#ff00ff',
			'--chakra-colors-hashtag-fgOnSurface': '#ff00ff',
			'--chakra-colors-text-hashtag': '#ff00ff',
			// Interactive
			'--chakra-colors-text-interactive': '#00ff88',
			'--chakra-colors-border-focus': '#00ff88',
		},
	},
	{
		id: 'rose-gold',
		name: 'Rose Gold',
		category: 'full',
		description: 'Elegant blush with metallic warmth',
		preview: {
			primary: '#2a1f1f',
			secondary: '#3d2d2d',
			accent: '#e8a0a0',
		},
		overrides: {
			// Backgrounds - warm rose
			'--chakra-colors-bg-canvas': '#352828',
			'--chakra-colors-bg-surface': '#2a1f1f',
			'--chakra-colors-bg-inset': '#3d2d2d',
			'--chakra-colors-bg-soft': '#4a3838',
			'--chakra-colors-bg-subtle': '#1f1515',
			'--chakra-colors-bg-topNav': '#2a1f1f',
			// Text - soft rose
			'--chakra-colors-text-default': '#f8f0f0',
			'--chakra-colors-text-soft': '#e0c8c8',
			'--chakra-colors-text-softer': '#c8a8a8',
			'--chakra-colors-text-softest': '#a08080',
			'--chakra-colors-text-onColor': '#2a1f1f',
			// Borders
			'--chakra-colors-border-default': 'rgba(224, 200, 200, 0.25)',
			'--chakra-colors-border-soft': 'rgba(224, 200, 200, 0.15)',
			'--chakra-colors-border-strong': 'rgba(224, 200, 200, 0.4)',
			// Primary - rose gold
			'--chakra-colors-primary-fg': '#e8a0a0',
			'--chakra-colors-primary-fgStrong': '#d08080',
			'--chakra-colors-primary-bg': '#d88888',
			'--chakra-colors-primary-bgHover': '#e8a0a0',
			'--chakra-colors-primary-bgActive': '#c07070',
			'--chakra-colors-primary-bgAlpha': 'rgba(232, 160, 160, 0.2)',
			'--chakra-colors-primary-bgAlphaHover': 'rgba(232, 160, 160, 0.3)',
			// Secondary
			'--chakra-colors-secondary-bg': '#f8f0f0',
			'--chakra-colors-secondary-bgHover': '#f0e0e0',
			'--chakra-colors-secondary-fg': '#f8f0f0',
			'--chakra-colors-secondary-fgOnSurface': '#2a1f1f',
			'--chakra-colors-secondary-bgAlpha': 'rgba(248, 240, 240, 0.15)',
			'--chakra-colors-secondary-bgAlphaHover': 'rgba(248, 240, 240, 0.25)',
			// Points - champagne
			'--chakra-colors-points-fg': '#f7e7ce',
			'--chakra-colors-points-fgHover': '#fff0d8',
			'--chakra-colors-points-fgOnSurface': '#f7e7ce',
			'--chakra-colors-points-bgAlpha': 'rgba(247, 231, 206, 0.2)',
			// Hashtags - soft gold
			'--chakra-colors-hashtag-fg': '#d4af37',
			'--chakra-colors-hashtag-fgOnSurface': '#d4af37',
			'--chakra-colors-text-hashtag': '#d4af37',
			// Interactive
			'--chakra-colors-text-interactive': '#e8a0a0',
			'--chakra-colors-border-focus': '#e8a0a0',
		},
	},
];

/** All available theme presets */
export const THEME_PRESETS: ThemePreset[] = [...accentPresets, ...fullThemePresets];

/** Get presets by category */
export const getPresetsByCategory = (category: ThemePresetCategory): ThemePreset[] => THEME_PRESETS.filter((preset) => preset.category === category);

/** Get a preset by ID */
export const getPresetById = (id: string): ThemePreset | undefined => THEME_PRESETS.find((preset) => preset.id === id);

/** Generate CSS string from overrides object */
export const generateCSSFromPreset = (preset: ThemePreset): string => {
	return Object.entries(preset.overrides)
		.map(([property, value]) => `${property}: ${value} !important;`)
		.join('\n\t');
};
